<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useToasts } from '@/composables/useToasts'
import { formatDateTime } from '@/utils'
import { useFloatingUI } from '@/composables/useFloatingUI'

const { t } = useI18n()
const { toastHistory, hasUnread, markViewed, refreshHistory } = useToasts()

const isOpen = ref(false)
const buttonRef = ref<HTMLElement | null>(null)
const { floatingEl, floatingStyles } = useFloatingUI(buttonRef)

function toggle() {
  if (!isOpen.value) {
    refreshHistory()
    markViewed()
  }
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

function onClickOutside(e: Event) {
  const el = (e.target as HTMLElement).closest('.notifications')
  if (!el) close()
}

onMounted(() => document.addEventListener('click', onClickOutside))
onUnmounted(() => document.removeEventListener('click', onClickOutside))
</script>

<template>
  <div class="notifications">
    <button
      ref="buttonRef"
      class="btn btn-ghost"
      type="button"
      :aria-expanded="isOpen"
      :aria-label="t('notifications')"
      @click.stop="toggle"
    >
      <span class="bell-icon icon icon-bell" aria-hidden="true"></span>
      <span v-if="hasUnread" class="notifications-dot" aria-hidden="true"></span>
      <span class="sr-only">{{ t('notifications') }}</span>
    </button>
    <div v-if="isOpen" ref="floatingEl" :style="floatingStyles" class="status-dropdown" role="region" :aria-label="t('notifications')">
      <div class="status-dropdown-content">
        <h3 class="status-dropdown-title">{{ t('notifications-title') }}</h3>
        <div class="status-dropdown-items">
          <div v-if="toastHistory.length === 0" class="status-dropdown-item status-item-empty">
            {{ t('notifications-empty') }}
          </div>
          <div
            v-for="(toast, i) in [...toastHistory].reverse()"
            :key="i"
            class="status-dropdown-item notifications-item notifications-card"
          >
            <span
              class="notifications-icon"
              :style="{ color: toast.type === 'success' ? '#10b981' : '#6b7280' }"
            >
              {{ toast.type === 'success' ? '✓' : 'ℹ' }}
            </span>
            <div class="notifications-content">
              <div class="notifications-message">{{ toast.message }}</div>
              <div v-if="toast.timestamp" class="notifications-time">
                {{ formatDateTime(new Date(toast.timestamp)) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
